import React, { memo } from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import SelectInput from '@/components/General/inputs/SelectInput';
import FilterInput from '@/components/Filters/FilterInput';
import { OPERATORS } from '@/utils/filters/advancedFilterUtils';

/**
 * Individual filter condition row component
 * Displays field name, operator selector, value input, and remove button
 */
const FilterCondition = ({ 
    condition, 
    onUpdate, 
    onRemove, 
    dynamicOptionsData = {},
    isFirst = false,
    logic = 'AND'
}) => {
    const { fieldName, fieldType, operator } = condition;
    const { theme } = useUnistyles();
    
    // Map field types to icons
    const getIconForFieldType = (type) => {
        const iconMap = {
            text: 'text',
            number: 'integer',
            date: 'calendar',
            'date-range': 'calendar-alt',
            boolean: 'toggle',
            select: 'list',
            sheetselector: 'list',
            multiselect: 'list',
        };
        return iconMap[type] || 'filter';
    };
    
    const fieldIcon = getIconForFieldType(fieldType);
    
    // Get operators for this field type
    const operators = OPERATORS[fieldType] || OPERATORS.text;
    const operatorOptions = operators.map(op => ({ label: op.label, value: op.value }));
    
    // Get dynamic options data for this condition if needed
    const conditionDynamicData = condition.dynamicOptions 
        ? dynamicOptionsData[condition.dynamicOptions] || []
        : [];

    return (
        <View style={styles.container}>
            {/* Logic indicator for non-first conditions */}
            {!isFirst && (
                <View style={styles.logicIndicator}>
                    <Text style={styles.logicText}>{logic}</Text>
                </View>
            )}
            
            <View style={styles.conditionCard}>
                {/* Header with field name and remove button */}
                <View style={styles.header}>
                    <View style={styles.fieldInfo}>
                        <View style={styles.fieldIconWrapper}>
                            {fieldType === 'number' ? (
                                <Text style={{ fontSize: 14, color: theme.colors.primary }}>123</Text>
                            ) : (
                                <Unicon name={fieldIcon} size={14} colorVariant="primary" />
                            )}
                        </View>
                        <View style={styles.fieldTextWrapper}>
                            <Text style={styles.fieldName}>{fieldName}</Text>
                        </View>
                    </View>
                    <Pressable 
                        onPress={onRemove} 
                        style={styles.removeButton}
                        hitSlop={12}
                    >
                        <Unicon name="close" size={22} colorVariant="textSecondary" />
                    </Pressable>
                </View>
                
                {/* Operator and Value Row */}
                <View style={styles.inputRow}>
                    {/* Operator Selector */}
                    <View style={styles.operatorContainer}>
                        <SelectInput
                            title="Condition"
                            value={operatorOptions.find(op => op.value === operator)}
                            options={operatorOptions}
                            onUpdate={(newOperator) => {
                                if(operator === 'past_n_days' || newOperator === 'past_n_days') {
                                    onUpdate({ operator: newOperator, value: '', value2: '' })
                                } else {
                                    onUpdate({ operator: newOperator })
                                }
                            }}
                        />
                    </View>
                    
                    {/* Value Input */}
                    <View style={styles.valueContainer}>
                        <FilterInput
                            condition={condition}
                            onValueChange={(newValue) => onUpdate({ value: newValue })}
                            onValue2Change={(newValue2) => onUpdate({ value2: newValue2 })}
                            dynamicOptionsData={conditionDynamicData}
                        />
                    </View>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }, rt) => ({
    container: {
        marginBottom: 16,
    },
    logicIndicator: {
        alignItems: 'center',
        marginBottom: 8,
    },
    logicText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.primary,
        textTransform: 'uppercase',
        letterSpacing: 1.5,
        paddingHorizontal: 10,
        paddingVertical: 2,
        borderRadius: 4,
    },
    conditionCard: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        borderWidth: 0,
        boxShadow: shadows.card,
        padding: 16,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    fieldInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flex: 1,
    },
    fieldIconWrapper: {
        width: 28,
        height: 28,
        borderRadius: 6,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    fieldTextWrapper: {
        flex: 1,
    },
    fieldName: {
        fontSize: 14,
        fontFamily: fonts.semibold,
        color: colors.text,
    },
    removeButton: {
        width: 32,
        height: 32,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
        _web: {
            cursor: 'pointer',
            transition: 'all 0.15s',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    inputRow: {
        flexDirection: rt.isPortrait ? 'column' : 'row',
        gap: 12,
        alignItems: rt.isPortrait ? 'flex-start' : 'flex-end',
    },
    operatorContainer: {
        minWidth: 160,
        flex: rt.isPortrait ? 1 : undefined,
        maxWidth: rt.isPortrait ? undefined : 180,
    },
    valueContainer: {
        flex: rt.isPortrait ? 1 : 2,
        minWidth: 200,
    },
}));

export default memo(FilterCondition);
